import { useEffect, useRef, useState } from 'react';
import ErrorMessage from './ErrorMessage';
import { DeploymentLogLevel } from '@back4app2/sdk';
import LoadingSpinner from './LoadingSpinner';
import Modal from "./Modal";
import LogLevelBadge, { LogLevel } from './ds/LogLevelBadge';
import { ExpandIcon } from './ds/icons';

export type LogBoxProps = {
  errorMessage?: string;
  logs?: {
    id: string;
    time: Date;
    message: string;
    level: DeploymentLogLevel;
  }[];
  isLoading: boolean;
  canMaximize?: boolean
}

const LogBox = ({ errorMessage, logs, isLoading, canMaximize }: LogBoxProps) => {
  const [autoScroll, setAutoScroll] = useState(true);
  const [isMaximized, setIsMaximized] = useState(() => !!document.fullscreenElement);
  const [isModalOpen, setIsModalOpen] = useState(false)
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(
    () => {
      if (!errorMessage && logs && autoScroll && boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
    },
    [
      errorMessage,
      logs,
      autoScroll
    ]
  );


  const exitHandler = (e: Event) => {
    if (!document.fullscreenElement) setIsMaximized(false);
  }

  useEffect(() => {    
    document.addEventListener('fullscreenchange', exitHandler, false);
    return () => {
      document.removeEventListener('fullscreenchange', exitHandler, false);
    }
  }, []);

  const onScroll = () => {
    if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) >= 0.99 * boxRef.current.scrollHeight && !autoScroll) {
      setAutoScroll(true);
    } else if (boxRef.current && (boxRef.current.scrollTop + boxRef.current.clientHeight) < 0.99 * boxRef.current.scrollHeight && autoScroll) {
      setAutoScroll(false);
    }
  };

  const handleFullScreen = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    if (isLoading || errorMessage) {
      return;
    }
    if (boxRef.current) {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
      try {
        await boxRef.current.requestFullscreen();
        setIsMaximized(true);
      } catch (err) {
        console.log('err in requesting div fullscreen', err);
        setIsModalOpen(true);
      }
    }
  }

  const exitFullScreen = async () => {
    if (document.fullscreenElement) {
      await document.exitFullscreen();
    }
    setIsMaximized(false);
  }

  /**
   * One line per entry: timestamp, level, message. The message wraps instead
   * of pushing the row sideways — a build log read on a phone was unreadable
   * when the only way through it was a horizontal scrollbar.
   */
  const renderLines = (inModal: boolean) => logs && logs.map((log, i) => (
    <div
      key={log.id}
      className={[
        'flex flex-col gap-1 px-3.5 py-[5px] sm:flex-row sm:items-start sm:gap-3',
        i % 2 ? 'bg-white/[0.015]' : '',
      ].join(' ')}
    >
      <span className="font-courrier text-ds-115 text-navy-300 flex-none sm:w-[132px]">
        {log.time.toISOString().slice(11, 23)}
      </span>
      <LogLevelBadge level={log.level.toLowerCase() as LogLevel} fixedWidth={!inModal} />
      <span className="font-courrier text-ds-115 leading-[1.5] text-navy-100 flex-1 min-w-0 break-words">
        {log.message}
      </span>
    </div>
  ));

  return (<div className="relative">
    {canMaximize && !isMaximized ? (
      <button
        type="button"
        aria-label="Expand log"
        onClick={isMaximized ? exitFullScreen : handleFullScreen}
        className="b4-focus z-10 absolute top-2.5 right-2.5 w-[30px] h-[30px] rounded-control border border-navy-400 bg-navy-700 text-navy-100 flex items-center justify-center hover:bg-navy-600 transition-colors duration-[120ms]"
      >
        <ExpandIcon size={14} />
      </button>
    ) : null}
    <div
      className="h-[26rem] relative py-1.5 bg-navy-950 overflow-y-auto b4-thin"
      ref={boxRef}
      onWheel={onScroll}
    >
      {isMaximized ? (
        <button
          type="button"
          onClick={exitFullScreen}
          className="b4-focus z-10 sticky top-[calc(100%-25px)] left-full text-ds-115 rounded-control border border-navy-400 bg-navy-700 text-navy-100 px-2 py-1"
        >
          Exit FullScreen
        </button>
      ) : null}
      {isLoading ? <div className="flex h-full justify-center items-center"><LoadingSpinner color="text-white" /></div> : null}
      {errorMessage ? (
        <div className="flex h-full"><ErrorMessage message={errorMessage} /></div>
      ) : renderLines(false)}
    </div>
    {canMaximize ? (
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)} theme='DARK'>
        <div className="bg-navy-950 rounded-card overflow-hidden">{renderLines(true)}</div>
      </Modal>
    ) : null}
  </div>);
}

export default LogBox;
